import { apiClient } from './apiClient';

export interface ConfigurationDto {
  configKey: string;
  configValue: string;
  description: string;
  category: string;
  lastModified: string;
  modifiedBy: string;
}

export interface UpdateConfigurationRequest {
  configValue: string;
  category?: string;
  modifiedBy?: string;
}

export interface BulkConfigurationUpdateRequest {
  configurations: ConfigurationUpdateItem[];
  modifiedBy?: string;
}

export interface ConfigurationUpdateItem {
  configKey: string;
  configValue: string;
  category?: string;
}

/**
 * What a successful configuration write has to say for itself. The write happened; anything here
 * is a consequence of it the person who saved should see.
 *
 * A value that cannot be accepted is a 400 and throws, so this is never a validation result. It
 * carries the one case the API deliberately accepts and cannot leave silent: an analysis rubric
 * edit that orphans a CAD scoring category, which otherwise just stops scoring.
 */
export interface ConfigurationUpdateResult {
  staleCadRescoreCategories: string[];
}

export interface BrandingSettings {
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string;
  faviconUrl: string;
  companyName: string;
  applicationTitle: string;
}

export interface AISettings {
  summarizationPrompt: string;
  analysisPrompt: string;
  chatbotSystemPrompt: string;
  chatbotMaxResults: number;
}

export interface IngestionSettings {
  filenamePattern: string;
  ingestionEnabled: boolean;
}

/**
 * The agency's CAD event report settings (tenant configuration category `CAD`).
 *
 * These govern every CAD record for the agency, whichever way the document arrived: a supervisor
 * attaching it on the call page, the assistant, and a bulk upload that matches it to its call.
 * There is deliberately no per-source copy of the apply or scoring policy.
 */
export interface CadNameTestRequest {
  audioSample: string;
  reportSample: string;
  audioPattern: string;
  reportPattern: string;
}

export interface CadNameTestResult {
  audioKey: string | null;
  reportKey: string | null;
  matches: boolean;
  audioError: string | null;
  reportError: string | null;
}

export interface CadSettings {
  enabled: boolean;
  /** Accept CAD report PDFs with the audio on the Upload page and match them to calls on their own. */
  bulkUploadEnabled: boolean;
  /** Days a CAD report that matched no call is kept. A string so a half-typed value is not coerced. */
  inboxRetentionDays: string;
  /** Pattern that picks the part of an AUDIO file name that identifies the call. Blank: the stored incident number. */
  audioNameRegex: string;
  /** Pattern that picks the part of a CAD REPORT file name that identifies the call. Blank: any part holding a digit. */
  reportNameRegex: string;
  readingNotes: string;
  autoVerify: boolean;
  verificationPrompt: string;
  autoApply: boolean;
  applyPolicy: 'FillEmpty' | 'Overwrite';
  /** Comma-separated call record field names. Empty means a CAD record fills no fields. */
  applyFields: string;
  autoRescore: boolean;
  /** Comma-separated rubric category names. Empty means a CAD record scores nothing. */
  rescoreCategories: string;
  rescorePrompt: string;
}



class ConfigurationService {
  async getConfigurationsByCategory(category: string): Promise<ConfigurationDto[]> {
    const response = await apiClient.get(`/api/configuration/categories/${category}`);
    return response.data;
  }

  async getConfiguration(configKey: string): Promise<ConfigurationDto> {
    const response = await apiClient.get(`/api/configuration/${configKey}`);
    return response.data;
  }

  async updateConfiguration(
    configKey: string,
    request: UpdateConfigurationRequest
  ): Promise<ConfigurationUpdateResult> {
    const response = await apiClient.put(`/api/configuration/${configKey}`, request);
    return this.updateResult(response.data);
  }

  async updateMultipleConfigurations(
    request: BulkConfigurationUpdateRequest
  ): Promise<ConfigurationUpdateResult> {
    const response = await apiClient.post('/api/configuration/bulk', request);
    return this.updateResult(response.data);
  }

  /** A write that reports nothing is the ordinary case, and an older API sent no body at all. */
  private updateResult(data: Partial<ConfigurationUpdateResult> | null): ConfigurationUpdateResult {
    return { staleCadRescoreCategories: data?.staleCadRescoreCategories ?? [] };
  }

  // Helper methods for specific settings groups
  async getBrandingSettings(): Promise<BrandingSettings> {
    const configs = await this.getConfigurationsByCategory('Branding');
    return {
      primaryColor: this.getConfigValue(configs, 'PrimaryColor', ''),
      secondaryColor: this.getConfigValue(configs, 'SecondaryColor', ''),
      logoUrl: this.getConfigValue(configs, 'LogoUrl', ''),
      faviconUrl: this.getConfigValue(configs, 'FaviconUrl', ''),
      companyName: this.getConfigValue(configs, 'CompanyName', ''),
      applicationTitle: this.getConfigValue(configs, 'ApplicationTitle', ''),
    };
  }

  async updateBrandingSettings(settings: BrandingSettings): Promise<void> {
    const configurations: ConfigurationUpdateItem[] = [
      { configKey: 'PrimaryColor', configValue: settings.primaryColor, category: 'Branding' },
      { configKey: 'SecondaryColor', configValue: settings.secondaryColor, category: 'Branding' },
      { configKey: 'LogoUrl', configValue: settings.logoUrl, category: 'Branding' },
      { configKey: 'FaviconUrl', configValue: settings.faviconUrl, category: 'Branding' },
      { configKey: 'CompanyName', configValue: settings.companyName, category: 'Branding' },
      {
        configKey: 'ApplicationTitle',
        configValue: settings.applicationTitle,
        category: 'Branding',
      },
    ];

    await this.updateMultipleConfigurations({ configurations, modifiedBy: 'User' });
  }

  async getIngestionSettings(): Promise<IngestionSettings> {
    const configs = await this.getConfigurationsByCategory('Ingestion');
    return {
      filenamePattern: this.getConfigValue(configs, 'FilenamePattern', ''),
      ingestionEnabled:
        this.getConfigValue(configs, 'IngestionEnabled', 'false').toUpperCase() === 'TRUE',
    };
  }

  async updateIngestionSettings(settings: IngestionSettings): Promise<void> {
    const configurations: ConfigurationUpdateItem[] = [
      { configKey: 'FilenamePattern', configValue: settings.filenamePattern, category: 'Ingestion' },
      { configKey: 'IngestionEnabled', configValue: settings.ingestionEnabled.toString().toUpperCase(), category: 'Ingestion' },
    ];
    await this.updateMultipleConfigurations({ configurations, modifiedBy: 'User' });
  }

  async getAISettings(): Promise<AISettings> {
    const configs = await this.getConfigurationsByCategory('AI');
    return {
      summarizationPrompt: this.getConfigValue(configs, 'SummarizationPrompt', ''),
      analysisPrompt: this.getConfigValue(configs, 'AnalysisPrompt', ''),
      chatbotSystemPrompt: this.getConfigValue(configs, 'ChatbotSystemPrompt', ''),
      chatbotMaxResults: parseInt(this.getConfigValue(configs, 'ChatbotMaxResults', '5')),
    };
  }

  /**
   * Only keys whose value actually changed are sent, for the same reason as `updateCadSettings`.
   * None of these four may be blank, and `getConfigValue` reports a missing or already-blank stored
   * row as `''`, so sending all four every time would let one such row refuse the whole batch -
   * including the rubric edit the admin actually made, on a tab that would then never save again.
   */
  async updateAISettings(
    settings: AISettings,
    baseline?: AISettings
  ): Promise<ConfigurationUpdateResult> {
    const all: ConfigurationUpdateItem[] = [
      {
        configKey: 'SummarizationPrompt',
        configValue: settings.summarizationPrompt,
        category: 'AI',
      },
      { configKey: 'AnalysisPrompt', configValue: settings.analysisPrompt, category: 'AI' },
      {
        configKey: 'ChatbotSystemPrompt',
        configValue: settings.chatbotSystemPrompt,
        category: 'AI',
      },
      {
        configKey: 'ChatbotMaxResults',
        configValue: settings.chatbotMaxResults.toString(),
        category: 'AI',
      },
    ];

    const configurations = baseline
      ? all.filter((item) => this.aiConfigValue(baseline, item.configKey) !== item.configValue)
      : all;

    if (configurations.length === 0) return { staleCadRescoreCategories: [] };

    return await this.updateMultipleConfigurations({ configurations, modifiedBy: 'User' });
  }

  /** The value a given AI key would be written with, for the changed-key comparison above. */
  private aiConfigValue(settings: AISettings, configKey: string): string | undefined {
    switch (configKey) {
      case 'SummarizationPrompt':
        return settings.summarizationPrompt;
      case 'AnalysisPrompt':
        return settings.analysisPrompt;
      case 'ChatbotSystemPrompt':
        return settings.chatbotSystemPrompt;
      case 'ChatbotMaxResults':
        return settings.chatbotMaxResults.toString();
      default:
        return undefined;
    }
  }

  async getCadSettings(): Promise<CadSettings> {
    const configs = await this.getConfigurationsByCategory('CAD');
    const bool = (key: string, fallback: string) =>
      this.getConfigValue(configs, key, fallback).trim().toUpperCase() === 'TRUE';
    return {
      enabled: bool('CadRecordsEnabled', 'FALSE'),
      bulkUploadEnabled: bool('CadBulkUploadEnabled', 'FALSE'),
      inboxRetentionDays: this.getConfigValue(configs, 'CadInboxRetentionDays', '30'),
      audioNameRegex: this.getConfigValue(configs, 'CadAudioNameRegex', ''),
      reportNameRegex: this.getConfigValue(configs, 'CadReportNameRegex', ''),
      readingNotes: this.getConfigValue(configs, 'CadReadingNotes', ''),
      autoVerify: bool('CadAutoVerify', 'TRUE'),
      verificationPrompt: this.getConfigValue(configs, 'CadVerificationPrompt', ''),
      autoApply: bool('CadAutoApply', 'FALSE'),
      applyPolicy:
        this.getConfigValue(configs, 'CadApplyPolicy', 'FillEmpty') === 'Overwrite'
          ? 'Overwrite'
          : 'FillEmpty',
      applyFields: this.getConfigValue(configs, 'CadApplyFields', ''),
      autoRescore: bool('CadAutoRescore', 'FALSE'),
      rescoreCategories: this.getConfigValue(configs, 'CadRescoreCategories', ''),
      rescorePrompt: this.getConfigValue(configs, 'CadRescorePrompt', ''),
    };
  }

  /**
   * One bulk write, so the keys land in a single transaction: a fault partway through must not
   * leave the agency with, say, automatic scoring on and its category list not yet written.
   *
   * Only keys whose value actually changed are sent. This is not an optimization. The bulk endpoint
   * validates the whole batch and refuses all of it if any member is invalid, so sending all ten
   * every time means one category the analysis rubric no longer defines would freeze the entire
   * screen, including the switch that turns CAD records off. Sending only what changed keeps an
   * unrelated stale value from blocking an unrelated edit.
   */
  async updateCadSettings(settings: CadSettings, baseline?: CadSettings): Promise<void> {
    const flag = (value: boolean) => (value ? 'TRUE' : 'FALSE');
    const all: ConfigurationUpdateItem[] = [
      { configKey: 'CadRecordsEnabled', configValue: flag(settings.enabled), category: 'CAD' },
      {
        configKey: 'CadBulkUploadEnabled',
        configValue: flag(settings.bulkUploadEnabled),
        category: 'CAD',
      },
      {
        configKey: 'CadInboxRetentionDays',
        configValue: settings.inboxRetentionDays.trim(),
        category: 'CAD',
      },
      { configKey: 'CadAudioNameRegex', configValue: settings.audioNameRegex.trim(), category: 'CAD' },
      { configKey: 'CadReportNameRegex', configValue: settings.reportNameRegex.trim(), category: 'CAD' },
      { configKey: 'CadReadingNotes', configValue: settings.readingNotes, category: 'CAD' },
      { configKey: 'CadAutoVerify', configValue: flag(settings.autoVerify), category: 'CAD' },
      {
        configKey: 'CadVerificationPrompt',
        configValue: settings.verificationPrompt,
        category: 'CAD',
      },
      { configKey: 'CadAutoApply', configValue: flag(settings.autoApply), category: 'CAD' },
      { configKey: 'CadApplyPolicy', configValue: settings.applyPolicy, category: 'CAD' },
      { configKey: 'CadApplyFields', configValue: settings.applyFields, category: 'CAD' },
      { configKey: 'CadAutoRescore', configValue: flag(settings.autoRescore), category: 'CAD' },
      {
        configKey: 'CadRescoreCategories',
        configValue: settings.rescoreCategories,
        category: 'CAD',
      },
      { configKey: 'CadRescorePrompt', configValue: settings.rescorePrompt, category: 'CAD' },
    ];

    const configurations = baseline
      ? all.filter((item) => {
          const before = this.cadConfigValue(baseline, item.configKey);
          return before === undefined || before !== item.configValue;
        })
      : all;

    if (configurations.length === 0) return;

    await this.updateMultipleConfigurations({ configurations, modifiedBy: 'User' });
  }

  /** The value a given CAD key would be written with, for the changed-key comparison above. */
  private cadConfigValue(settings: CadSettings, configKey: string): string | undefined {
    const flag = (value: boolean) => (value ? 'TRUE' : 'FALSE');
    switch (configKey) {
      case 'CadRecordsEnabled':
        return flag(settings.enabled);
      case 'CadBulkUploadEnabled':
        return flag(settings.bulkUploadEnabled);
      case 'CadInboxRetentionDays':
        return settings.inboxRetentionDays.trim();
      case 'CadAudioNameRegex':
        return settings.audioNameRegex.trim();
      case 'CadReportNameRegex':
        return settings.reportNameRegex.trim();
      case 'CadReadingNotes':
        return settings.readingNotes;
      case 'CadAutoVerify':
        return flag(settings.autoVerify);
      case 'CadVerificationPrompt':
        return settings.verificationPrompt;
      case 'CadAutoApply':
        return flag(settings.autoApply);
      case 'CadApplyPolicy':
        return settings.applyPolicy;
      case 'CadApplyFields':
        return settings.applyFields;
      case 'CadAutoRescore':
        return flag(settings.autoRescore);
      case 'CadRescoreCategories':
        return settings.rescoreCategories;
      case 'CadRescorePrompt':
        return settings.rescorePrompt;
      default:
        return undefined;
    }
  }

  /**
   * What the two name patterns make of one recording name and one report name, decided by the server
   * so the answer is exactly what the matching will do. Reads no call data. PlatformAdmin only.
   */
  async testCadNames(request: CadNameTestRequest): Promise<CadNameTestResult> {
    const response = await apiClient.post('/api/configuration/cad/name-test', request);
    return response.data;
  }

  /** The scoring categories this agency's analysis rubric defines. PlatformAdmin only. */
  async getCadRubricCategories(): Promise<string[]> {
    const response = await apiClient.get('/api/configuration/cad/rubric-categories');
    return response.data;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async testAIAuditEvaluation(transcription: string): Promise<any> {
    const response = await apiClient.post('/api/ai-audit/test', {
      transcription,
    });
    return response.data;
  }

  private getConfigValue(configs: ConfigurationDto[], key: string, defaultValue: string): string {
    const config = configs.find((c) => c.configKey === key);
    // Return default if config doesn't exist, is null, undefined, or empty string
    return config?.configValue && config.configValue.trim() !== ''
      ? config.configValue
      : defaultValue;
  }

  async getPlatformAdminEmail(): Promise<string> {
    const response = await apiClient.get('/api/configuration/platform-admin-email');
    return response.data;
  }
}

export const configurationService = new ConfigurationService();
